/**
 * Predictive Search functionality for Shopify themes
 */

class PredictiveSearch {
  constructor() {
    this.abortController = new AbortController();
    this.debounceTimer = null;
    this.searchInput = null;
    this.resultsContainer = null;
    this.isOpen = false;
    
    this.init();
  }

  init() {
    // Find search elements
    this.searchInput = document.querySelector('input[type="search"], .search-input');
    this.resultsContainer = document.querySelector('.predictive-search-results, .search-results');
    
    if (!this.searchInput) return;
    
    // Create results container if it doesn't exist
    if (!this.resultsContainer) {
      this.createResultsContainer();
    }
    
    this.setupEventListeners();
  }

  createResultsContainer() {
    this.resultsContainer = document.createElement('div');
    this.resultsContainer.className = 'predictive-search-results';
    this.resultsContainer.innerHTML = `
      <div class="predictive-search-loading" style="display: none;">
        <div class="loading-spinner"></div>
        <span>Searching...</span>
      </div>
      <div class="predictive-search-content"></div>
    `;
    
    // Insert after search input
    this.searchInput.parentNode.insertBefore(this.resultsContainer, this.searchInput.nextSibling);
  }

  setupEventListeners() {
    if (!this.searchInput) return;
    
    // Search input events
    this.searchInput.addEventListener('input', this.handleInput.bind(this));
    this.searchInput.addEventListener('focus', this.handleFocus.bind(this));
    this.searchInput.addEventListener('blur', this.handleBlur.bind(this));
    
    // Close on outside click
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.predictive-search, .search-form')) {
        this.close();
      }
    });
    
    // Keyboard navigation
    this.searchInput.addEventListener('keydown', this.handleKeydown.bind(this));
  }

  handleInput(event) {
    const query = event.target.value.trim();
    
    // Clear previous timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    
    // Debounce search requests
    this.debounceTimer = setTimeout(() => {
      if (query.length >= 2) {
        this.performSearch(query);
      } else {
        this.close();
      }
    }, 300);
  }

  handleFocus() {
    const query = this.searchInput.value.trim();
    if (query.length >= 2) {
      this.open();
    }
  }

  handleBlur() {
    // Delay closing to allow clicks on results
    setTimeout(() => this.close(), 150);
  }

  handleKeydown(event) {
    if (!this.isOpen) return;
    
    const results = this.resultsContainer.querySelectorAll('.search-result-item');
    const currentIndex = Array.from(results).findIndex(item => 
      item.classList.contains('highlighted')
    );
    
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.highlightResult(currentIndex + 1, results);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.highlightResult(currentIndex - 1, results);
        break;
      case 'Enter':
        event.preventDefault();
        const highlighted = this.resultsContainer.querySelector('.search-result-item.highlighted');
        if (highlighted) {
          const link = highlighted.querySelector('a');
          if (link) link.click();
        }
        break;
      case 'Escape':
        this.close();
        break;
    }
  }

  highlightResult(index, results) {
    // Remove existing highlights
    results.forEach(item => item.classList.remove('highlighted'));
    
    // Add highlight to new item
    if (index >= 0 && index < results.length) {
      results[index].classList.add('highlighted');
    }
  }

  async performSearch(query) {
    try {
      // Abort previous request
      this.abortController.abort();
      this.abortController = new AbortController();
      
      this.showLoading();
      
      // Construct search URL
      const searchUrl = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=10`;
      
      const response = await fetch(searchUrl, {
        signal: this.abortController.signal
      });
      
      if (!response.ok) throw new Error('Search failed');
      
      const data = await response.json();
      this.displayResults(data, query);
      
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Search error:', error);
        this.hideLoading();
      }
    }
  }

  displayResults(data, query) {
    this.hideLoading();
    
    const products = data.resources?.results?.products || [];
    const content = this.resultsContainer.querySelector('.predictive-search-content');
    
    if (products.length === 0) {
      content.innerHTML = `
        <div class="no-results">
          <p>No products found for "${query}"</p>
        </div>
      `;
    } else {
      content.innerHTML = `
        <div class="search-results-list">
          ${products.map((product, index) => `
            <div class="search-result-item ${index === 0 ? 'highlighted' : ''}" data-product-id="${product.id}">
              <a href="${product.url}" class="search-result-link">
                ${product.featured_image ? `
                  <div class="search-result-image">
                    <img src="${product.featured_image.url}" alt="${product.title}" width="60" height="60">
                  </div>
                ` : ''}
                <div class="search-result-content">
                  <h3 class="search-result-title">${product.title}</h3>
                  <div class="search-result-price">
                    ${product.price_min ? this.formatPrice(product.price_min) : ''}
                  </div>
                </div>
              </a>
            </div>
          `).join('')}
        </div>
        <div class="search-results-footer">
          <a href="/search?q=${encodeURIComponent(query)}" class="view-all-link">
            View all results for "${query}"
          </a>
        </div>
      `;
    }
    
    this.open();
  }

  formatPrice(price) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price / 100);
  }

  showLoading() {
    const loading = this.resultsContainer.querySelector('.predictive-search-loading');
    const content = this.resultsContainer.querySelector('.predictive-search-content');
    
    if (loading) loading.style.display = 'block';
    if (content) content.innerHTML = '';
    
    this.open();
  }

  hideLoading() {
    const loading = this.resultsContainer.querySelector('.predictive-search-loading');
    if (loading) loading.style.display = 'none';
  }

  open() {
    if (!this.resultsContainer) return;
    
    this.resultsContainer.classList.add('active');
    this.isOpen = true;
  }

  close() {
    if (!this.resultsContainer) return;
    
    this.resultsContainer.classList.remove('active');
    this.isOpen = false;
  }
}

// Initialize predictive search
document.addEventListener('DOMContentLoaded', () => {
  new PredictiveSearch();
});

// Export for use in other modules
window.PredictiveSearch = PredictiveSearch;